/**
 * Cart variant edit modal (adapted from diagmart cart edit UX).
 */
class CartEdit {
  constructor() {
    this.modal = document.getElementById('Cart-Edit-Modal');
    if (!this.modal) return;

    this.content = this.modal.querySelector('[data-cart-edit-content]');
    this.lineKey = null;
    this.lineIndex = null;
    this.quantity = 1;
    this.variants = [];
    this.selectedOptions = [];

    document.addEventListener('click', this.onDocumentClick.bind(this));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.modal.hasAttribute('hidden')) {
        this.close();
      }
    });
  }

  onDocumentClick(event) {
    const trigger = event.target.closest('[data-cart-edit]');
    if (trigger) {
      event.preventDefault();
      this.open(trigger);
      return;
    }

    if (event.target.closest('[data-cart-edit-close]')) {
      event.preventDefault();
      this.close();
    }
  }

  open(trigger) {
    if (trigger.classList.contains('loading')) return;

    const url = trigger.dataset.url;
    this.lineKey = trigger.dataset.key;
    this.lineIndex = trigger.dataset.line;
    this.quantity = parseInt(trigger.dataset.quantity, 10) || 1;

    if (!url) return;

    trigger.classList.add('loading');

    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const template = doc.querySelector('[data-cart-edit-template]');
        if (!template) throw new Error('Cart edit template missing');

        this.content.innerHTML = template.outerHTML;
        this.bindTemplate();
        this.modal.removeAttribute('hidden');
        document.body.classList.add('cart-edit-open');
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        trigger.classList.remove('loading');
      });
  }

  close() {
    this.modal.setAttribute('hidden', '');
    document.body.classList.remove('cart-edit-open');
    this.content.innerHTML = '';
  }

  bindTemplate() {
    const root = this.content.querySelector('[data-cart-edit-template]');
    const form = root.querySelector('[data-cart-edit-form]');
    const qtyInput = root.querySelector('[data-cart-edit-quantity]');
    const variantsScript = root.querySelector('[data-cart-edit-variants]');

    if (qtyInput) qtyInput.value = this.quantity;

    const quantityEl = root.querySelector('.quantity');
    if (quantityEl && typeof QuantityInput !== 'undefined') {
      new QuantityInput(quantityEl);
    }

    try {
      this.variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      this.variants = [];
    }

    this.selectedOptions = [];
    root.querySelectorAll('.cart-edit__swatch').forEach((swatch) => {
      const checked = swatch.querySelector('input:checked');
      this.selectedOptions[parseInt(swatch.dataset.optionIndex, 10)] = checked ? checked.value : null;
    });

    root.querySelectorAll('[data-cart-edit-option]').forEach((input) => {
      input.addEventListener('change', () => {
        const position = parseInt(input.dataset.optionPosition, 10);
        this.selectedOptions[position] = input.value;
        const header = input.closest('.cart-edit__swatch').querySelector('[data-header-option]');
        if (header) header.textContent = input.value;
        this.updateVariantUI(root);
      });
    });

    this.updateVariantUI(root);
    form.addEventListener('submit', (event) => this.onSubmit(event, root));
  }

  findVariant() {
    return this.variants.find((variant) => {
      return this.selectedOptions.every((value, index) => {
        if (value == null) return true;
        return variant[`option${index + 1}`] === value;
      });
    });
  }

  updateVariantUI(root) {
    const variant = this.findVariant();
    const variantInput = root.querySelector('[data-cart-edit-variant-id]');
    const priceEl = root.querySelector('[data-cart-edit-price]');
    const compareEl = root.querySelector('[data-cart-edit-compare]');
    const labelEl = root.querySelector('[data-cart-edit-option-label]');
    const imageEl = root.querySelector('[data-cart-edit-image]');
    const submitBtn = root.querySelector('[data-cart-edit-submit]');

    this.disableUnavailableOptions(root);

    if (!variant) {
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.querySelector('span:not(.loading-overlay)').textContent = theme.variantStrings.unavailable;
      }
      return;
    }

    if (variantInput) variantInput.value = variant.id;
    if (priceEl) priceEl.textContent = variant.price_formatted;
    if (labelEl) labelEl.textContent = variant.title;

    if (compareEl) {
      if (variant.compare_at_price_formatted) {
        compareEl.textContent = variant.compare_at_price_formatted;
        compareEl.classList.remove('hide');
      } else {
        compareEl.textContent = '';
        compareEl.classList.add('hide');
      }
    }

    if (imageEl && variant.featured_image) {
      imageEl.src = variant.featured_image;
    }

    if (submitBtn) {
      submitBtn.disabled = !variant.available;
      submitBtn.querySelector('span:not(.loading-overlay)').textContent = variant.available
        ? theme.variantStrings.addToCart
        : theme.variantStrings.soldOut;
    }
  }

  disableUnavailableOptions(root) {
    root.querySelectorAll('.cart-edit__swatch').forEach((swatch) => {
      const optionIndex = parseInt(swatch.dataset.optionIndex, 10);
      swatch.querySelectorAll('.cart-edit__swatch-element').forEach((element) => {
        const value = element.dataset.value;
        const available = this.variants.some((variant) => {
          if (!variant.available) return false;
          if (variant[`option${optionIndex + 1}`] !== value) return false;
          return this.selectedOptions.every((selected, index) => {
            if (index === optionIndex || selected == null) return true;
            return variant[`option${index + 1}`] === selected;
          });
        });

        element.classList.toggle('soldout', !available);
        const input = element.querySelector('input');
        if (input) input.disabled = !available;
      });
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }
    ];
  }

  getSectionNames() {
    return this.getSectionsToRender().map((section) => section.section);
  }

  getSectionInnerHTML(html, selector) {
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : '';
  }

  fetchSections() {
    const sectionNames = this.getSectionNames().join(',');
    const url = `${window.location.pathname}?sections=${sectionNames}`;

    return fetch(url)
      .then((response) => response.json())
      .then((sections) => ({ sections }));
  }

  renderSections(parsedState) {
    const render = (state) => {
      if (!state || !state.sections) return;

      this.getSectionsToRender().forEach((section) => {
        const sectionHtml = state.sections[section.section];
        if (!sectionHtml) return;

        const container = document.getElementById(section.id);
        if (!container) return;

        const elementToReplace = container.querySelector(section.selector) || container;
        const innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);

        if (!innerHTML) return;

        elementToReplace.innerHTML = innerHTML;

        elementToReplace.querySelectorAll('.quantity:not(.buttons_added)').forEach((quantity) => {
          if (typeof QuantityInput !== 'undefined') new QuantityInput(quantity);
        });
      });

      if (typeof CartDrawer !== 'undefined') {
        new CartDrawer();
      }
      if (typeof Cart !== 'undefined' && document.getElementById('Cart')) {
        new Cart();
      }

      // Keep drawer open only when editing from the drawer, not on the cart page.
      const isCartPage = document.body.classList.contains('template-cart');
      const drawer = document.getElementById('Cart-Drawer');
      if (!isCartPage && drawer) {
        document.body.classList.add('open-cc');
        drawer.classList.add('active');
      } else if (isCartPage && drawer) {
        drawer.classList.remove('active');
        document.body.classList.remove('open-cc');
      }
    };

    if (parsedState && parsedState.sections) {
      render(parsedState);
      return Promise.resolve();
    }

    return this.fetchSections().then(render);
  }

  onSubmit(event, root) {
    event.preventDefault();

    const submitBtn = root.querySelector('[data-cart-edit-submit]');
    if (!submitBtn || submitBtn.disabled || submitBtn.classList.contains('loading')) return;

    const variant = this.findVariant();
    const quantity = parseInt(root.querySelector('[data-cart-edit-quantity]').value, 10) || 1;
    if (!variant || !variant.available) return;

    submitBtn.classList.add('loading');
    submitBtn.setAttribute('aria-disabled', 'true');

    const sections = this.getSectionNames();
    const sectionsUrl = window.location.pathname;

    const removeBody = JSON.stringify({
      line: parseInt(this.lineIndex, 10),
      quantity: 0,
      sections,
      sections_url: sectionsUrl
    });

    fetch(`${theme.routes.cart_change_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: removeBody
    })
      .then((response) => response.json())
      .then(() => {
        const formData = new FormData();
        formData.append('id', String(variant.id));
        formData.append('quantity', String(quantity));
        formData.append('sections', sections);
        formData.append('sections_url', sectionsUrl);

        return fetch(`${theme.routes.cart_add_url}`, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript'
          },
          body: formData
        });
      })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status) {
          throw new Error(parsedState.description || 'Unable to update cart');
        }

        return this.renderSections(parsedState).then(() => {
          this.close();
        });
      })
      .catch((error) => {
        console.error(error);
        this.fetchSections()
          .then((state) => this.renderSections(state))
          .then(() => this.close());
      })
      .finally(() => {
        submitBtn.classList.remove('loading');
        submitBtn.removeAttribute('aria-disabled');
      });
  }
}

window.addEventListener('load', () => {
  new CartEdit();
});
